import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";

interface PopoverProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  className?: string;
  align?: "start" | "end";
  /**
   * Dirección de apertura respecto al disparador. "down" (por defecto) abre
   * debajo; "up" abre encima — útil cuando el disparador está al pie del panel
   * y el menú se saldría de la pantalla por abajo.
   */
  direction?: "down" | "up";
  anchorRef?: RefObject<HTMLElement | null>;
  portal?: boolean;
}

/** Popover anchored by parent positioning or by a portal anchor. */
export function Popover({
  open,
  onClose,
  children,
  className = "",
  align = "start",
  direction = "down",
  anchorRef,
  portal = false,
}: PopoverProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [portalStyle, setPortalStyle] = useState<CSSProperties | null>(null);

  useLayoutEffect(() => {
    if (!open || !portal || !anchorRef?.current) {
      setPortalStyle(null);
      return undefined;
    }
    const updatePosition = () => {
      const rect = anchorRef.current?.getBoundingClientRect();
      if (!rect) return;
      setPortalStyle({
        // "up": anclado por el borde inferior al tope del disparador; "down":
        // por el borde superior a su base. `position: fixed` en ambos casos.
        top: direction === "up" ? undefined : rect.bottom + 8,
        bottom:
          direction === "up" ? window.innerHeight - rect.top + 8 : undefined,
        left: align === "start" ? rect.left : undefined,
        right: align === "end" ? window.innerWidth - rect.right : undefined,
      });
    };
    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [align, anchorRef, direction, open, portal]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        ref.current?.contains(target) ||
        anchorRef?.current?.contains(target)
      ) {
        return;
      }
      onClose();
    };
    const onEsc = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    setTimeout(() => document.addEventListener("mousedown", onDoc), 0);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onEsc);
    };
  }, [anchorRef, open, onClose]);
  if (!open) return null;

  const alignmentClass = portal ? "" : align === "end" ? "right-0" : "left-0";
  // Posicionamiento vertical en modo NO-portal: encima (bottom-full) o debajo
  // (top-full) del disparador según `direction`.
  const verticalClass = portal
    ? ""
    : direction === "up"
      ? "absolute bottom-full z-30 mb-2"
      : "absolute top-full z-30 mt-2";
  const content = (
    <div
      ref={ref}
      className={`${portal ? "fixed z-[80]" : verticalClass} bg-elevated border border-border-strong rounded-lg shadow-lg ${
        alignmentClass
      } ${className}`}
      style={
        portal
          ? {
              ...portalStyle,
              visibility: portalStyle ? "visible" : "hidden",
            }
          : undefined
      }
      role="dialog"
    >
      {children}
    </div>
  );

  if (portal && typeof document !== "undefined") {
    return createPortal(content, document.body);
  }
  return content;
}
